import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { initClickSound } from "./utils/clickSound";

// Pages
import HomePage from "./pages/HomePage";
import PlatformPage from "./pages/PlatformPage";
import PositiveReviewsPage from "./pages/PositiveReviewsPage";
import ReviewShieldPage from "./pages/ReviewShieldPage";
import GlobalReviewPage from "./pages/GlobalReviewPage";
import RR360Page from "./pages/RR360Page";
import GuestCommunicationPage from "./pages/GuestCommunicationPage";
import InsightsPage from "./pages/InsightsPage";
import HardwarePage from "./pages/HardwarePage";
import ImplementationPage from "./pages/ImplementationPage";
import PortalPage from "./pages/PortalPage";
import ContactPage from "./pages/ContactPage";
import SupportingDataPage from "./pages/SupportingDataPage";
import ReviewCulturePage from "./pages/ReviewCulturePage";
import TapNWinPage from "./pages/TapNWinPage";

// Marcom Module
import MarcoDashboard from "./modules/marco/MarcoDashboard";
import MarcoCreate from "./modules/marco/MarcoCreate";
import MarcoView from "./modules/marco/MarcoView";
import MarcoAdmin from "./modules/marco/MarcoAdmin";
import MarcoLibrary from "./modules/marco/MarcoLibrary";
import ChangePassword from "./modules/marco/ChangePassword";

// Layout wrapper for pages with header/footer
const Layout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

// Portal uses its own layout (no header/footer)
const PortalLayout = ({ children }) => (
  <main>{children}</main>
);

// Protected route - redirects to portal if not authenticated
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-wine border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/portal" replace />;
  if (user.must_change_password && !window.location.pathname.includes('change-password')) {
    return <Navigate to="/marcom/change-password" replace />;
  }
  return children;
};

function App() {
  useEffect(() => {
    return initClickSound();
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="App font-body">
          <BrowserRouter>
            <Routes>
              {/* Main pages with header/footer */}
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/platform" element={<Layout><PlatformPage /></Layout>} />
              <Route path="/positive-reviews" element={<Layout><PositiveReviewsPage /></Layout>} />
              <Route path="/review-shield" element={<Navigate to="/b-heard" replace />} />
              <Route path="/b-heard" element={<Layout><ReviewShieldPage /></Layout>} />
              <Route path="/global-review" element={<Layout><GlobalReviewPage /></Layout>} />
              <Route path="/rr360" element={<Layout><RR360Page /></Layout>} />
              <Route path="/guest-communication" element={<Layout><GuestCommunicationPage /></Layout>} />
              <Route path="/insights" element={<Layout><InsightsPage /></Layout>} />
              <Route path="/hardware" element={<Layout><HardwarePage /></Layout>} />
              <Route path="/implementation" element={<Layout><ImplementationPage /></Layout>} />
              <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
              <Route path="/supporting-data" element={<Layout><SupportingDataPage /></Layout>} />

              {/* Tap'N Win — EN / FR / ES */}
              <Route path="/en/tap-n-win" element={<Layout><TapNWinPage /></Layout>} />
              <Route path="/fr/tapez-et-gagnez" element={<Layout><TapNWinPage /></Layout>} />
              <Route path="/es/tap-n-win" element={<Layout><TapNWinPage /></Layout>} />

              {/* Review Culture — EN */}
              <Route path="/review-culture" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/review-culture/why-being-heard-matters" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/review-culture/research" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/review-culture/industries" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/review-culture/how-b-heard-works" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/review-culture/measuring-results" element={<Layout><ReviewCulturePage /></Layout>} />

              {/* Review Culture — FR */}
              <Route path="/fr/culture-des-avis" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/fr/culture-des-avis/pourquoi-etre-entendu" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/fr/culture-des-avis/recherche" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/fr/culture-des-avis/secteurs" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/fr/culture-des-avis/comment-fonctionne-b-heard" element={<Layout><ReviewCulturePage /></Layout>} />
              <Route path="/fr/culture-des-avis/mesurer-les-resultats" element={<Layout><ReviewCulturePage /></Layout>} />

              {/* Portal login */}
              <Route path="/portal" element={<PortalLayout><PortalPage /></PortalLayout>} />

              {/* Marcom Module (Protected) */}
              <Route path="/marcom" element={<ProtectedRoute><MarcoDashboard /></ProtectedRoute>} />
              <Route path="/marcom/create" element={<ProtectedRoute><MarcoCreate /></ProtectedRoute>} />
              <Route path="/marcom/library" element={<ProtectedRoute><MarcoLibrary /></ProtectedRoute>} />
              <Route path="/marcom/view/:id" element={<ProtectedRoute><MarcoView /></ProtectedRoute>} />
              <Route path="/marcom/admin" element={<ProtectedRoute><MarcoAdmin /></ProtectedRoute>} />
              <Route path="/marcom/change-password" element={<PortalLayout><ChangePassword /></PortalLayout>} />

              {/* Legacy /marco redirects */}
              <Route path="/marco" element={<Navigate to="/marcom" replace />} />
              <Route path="/marco/*" element={<Navigate to={window.location.pathname.replace('/marco', '/marcom')} replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
